/**
 * Installer API client.
 *
 * Talks ONLY to the new-app installer module (`/v1/installer/*`). The legacy
 * MIS contract lives entirely server-side — this client never holds legacy
 * creds or the legacy base URL (see INSTALLER_SURVEY_MERGE_PLAN.md §3, §5).
 *
 * Auth: every call (except the /auth/* endpoints) carries
 *   Authorization: Bearer <sessionToken>
 * where the token is the opaque new-app installer session token minted by the
 * server and stored in @capacitor/preferences under `wattr.installer.session`.
 * On a 401 the installerAuth store clears the session and bounces to login.
 *
 * Response shape mirrors services/api.ts: the server wraps payloads in a
 * `{ data }` envelope, so the typed wrappers unwrap `.data.data`.
 */
import axios, { type AxiosInstance } from 'axios';
import { Preferences } from '@capacitor/preferences';
import { API_BASE_URL } from '../config';

/** Preferences key holding the opaque installer session token. */
export const INSTALLER_SESSION_KEY = 'wattr.installer.session';

export const installerHttp: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/v1/installer`,
  timeout: 15_000,
});

// Inject the Bearer session token on every request. Read from Preferences so
// the token survives app restarts and a store re-hydrate isn't required before
// the first call after boot.
installerHttp.interceptors.request.use(async (config) => {
  const { value } = await Preferences.get({ key: INSTALLER_SESSION_KEY });
  if (value) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${value}`;
  }
  return config;
});

/** Unwrap the server's `{ data }` envelope. */
function unwrap<T>(body: { data: T } | T): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

// ── Personas ────────────────────────────────────────────────────────────
export type InstallerPersona = 'fcm' | 'contract';

// ── Auth ────────────────────────────────────────────────────────────────

export interface LoginResponse {
  /** Opaque new-app installer session token (Bearer). */
  sessionToken: string;
  persona: InstallerPersona;
  /** Legacy ClientFId — present for FCM only. */
  clientFid?: number | null;
  phone?: string | null;
}

export interface MeResponse {
  persona: InstallerPersona;
  clientFid?: number | null;
  phone?: string | null;
}

/** FCM legacy login → new-app session. POST /auth/login. No session required. */
export async function loginFcm(username: string, password: string): Promise<LoginResponse> {
  const { data } = await installerHttp.post<{ data: LoginResponse }>('/auth/login', {
    username,
    password,
  });
  return unwrap(data);
}

/** Contract worker phone identity → new-app session. POST /auth/phone. */
export async function loginContractPhone(phone: string): Promise<LoginResponse> {
  const { data } = await installerHttp.post<{ data: LoginResponse }>('/auth/phone', {
    phone,
  });
  return unwrap(data);
}

/** Drop the server-side Redis session. POST /auth/logout. */
export async function logout(): Promise<void> {
  await installerHttp.post('/auth/logout');
}

/** Current persona + identity. GET /me. */
export async function getMe(): Promise<MeResponse> {
  const { data } = await installerHttp.get<{ data: MeResponse }>('/me');
  return unwrap(data);
}

// ── Reads ───────────────────────────────────────────────────────────────

/**
 * A pump as returned by the installer search / pending-pumps endpoints.
 *
 * The new-app `/v1/installer/pumps/search` + `/pending-pumps` brokers emit the
 * legacy MIS row shape in **PascalCase** (`PumpFId`, `ImeiNumber`, `VoltageR`,
 * `StatusToShow`, …). The app reads camelCase, so every pump leaving this module
 * is run through `normalizePump()` first — this is the camelCase contract the
 * screens + PumpVitalsPanel consume. Vital / fault fields are optional because
 * the native pump-search path (id/name/status only) omits them.
 */
export interface InstallerPump {
  // ── Identifiers ──────────────────────────────────────────────────────────
  pumpFId: number;
  imeiNumber: string;
  deviceId: number;
  clientFId?: number;
  pumpName?: string;

  // ── Geography names ──────────────────────────────────────────────────────
  villageName?: string;
  districtName?: string;
  stateName?: string;

  // ── Status ───────────────────────────────────────────────────────────────
  statusToShow?: string;
  actualPumpStatus?: string;
  pumpStatus?: boolean;
  horsePower?: number;

  // ── Live electrical vitals ───────────────────────────────────────────────
  voltageR?: number;
  voltageY?: number;
  voltageB?: number;
  currentR?: number;
  currentY?: number;
  currentB?: number;
  powerFactor?: number;
  discharge?: number;
  temperature?: number;
  batteryPercentage?: number;
  signalStrengthPercentage?: number;

  // ── Fault flags ──────────────────────────────────────────────────────────
  dryRunFault?: boolean;
  overloadFault?: boolean;
  overVoltageFault?: boolean;
  underVoltageFault?: boolean;
  reversePhaseFault?: boolean;
  phaseDownFault?: boolean;
  overHeatFault?: boolean;

  /** Catch-all for any extra legacy keys (PascalCase or camelCase) that slip through. */
  [k: string]: unknown;
}

/** Read the first defined/non-empty value among the given keys off a raw row. */
function rawPick(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/** Coerce to a finite number, or undefined if absent / non-numeric. */
function rawNum(row: Record<string, unknown>, ...keys: string[]): number | undefined {
  const v = rawPick(row, ...keys);
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Coerce a legacy flag (true/1/"true"/"1") to a boolean, or undefined if absent. */
function rawBool(row: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  const v = rawPick(row, ...keys);
  if (v === undefined) return undefined;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') return v === 'true' || v === '1';
  return undefined;
}

/** Coerce to a trimmed non-empty string, or undefined. */
function rawStr(row: Record<string, unknown>, ...keys: string[]): string | undefined {
  const v = rawPick(row, ...keys);
  if (v == null) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

/**
 * Map a raw installer pump row (legacy PascalCase, see server `LegacyPump`) to
 * the camelCase `InstallerPump` the app consumes. PascalCase is read first
 * (what the broker actually sends), with the camelCase key as a defensive
 * fallback so this survives a future server-side rename.
 */
export function normalizePump(raw: unknown): InstallerPump {
  const row = (raw ?? {}) as Record<string, unknown>;

  const pump: InstallerPump = {
    // Identifiers
    pumpFId: rawNum(row, 'PumpFId', 'pumpFId') ?? 0,
    imeiNumber: rawStr(row, 'ImeiNumber', 'imeiNumber') ?? '',
    deviceId: rawNum(row, 'DeviceId', 'deviceId') ?? 0,
    clientFId: rawNum(row, 'ClientFId', 'clientFId'),
    pumpName: rawStr(row, 'PumpName', 'Pump', 'pumpName'),

    // Geography names
    villageName: rawStr(row, 'VillageName', 'villageName'),
    districtName: rawStr(row, 'DistrictName', 'districtName'),
    stateName: rawStr(row, 'StateName', 'stateName'),

    // Status
    statusToShow: rawStr(row, 'StatusToShow', 'statusToShow'),
    actualPumpStatus: rawStr(row, 'ActualPumpStatus', 'actualPumpStatus'),
    pumpStatus: rawBool(row, 'PumpStatus', 'pumpStatus'),
    horsePower: rawNum(row, 'HorsePower', 'horsePower'),

    // Live electrical vitals
    voltageR: rawNum(row, 'VoltageR', 'voltageR'),
    voltageY: rawNum(row, 'VoltageY', 'voltageY'),
    voltageB: rawNum(row, 'VoltageB', 'voltageB'),
    currentR: rawNum(row, 'CurrentR', 'currentR'),
    currentY: rawNum(row, 'CurrentY', 'currentY'),
    currentB: rawNum(row, 'CurrentB', 'currentB'),
    powerFactor: rawNum(row, 'PowerFactor', 'powerFactor'),
    discharge: rawNum(row, 'Discharge', 'discharge'),
    temperature: rawNum(row, 'Temperature', 'temperature'),
    batteryPercentage: rawNum(row, 'BatteryPercentage', 'batteryPercentage'),
    signalStrengthPercentage: rawNum(row, 'SignalStrengthPercentage', 'signalStrengthPercentage'),

    // Fault flags
    dryRunFault: rawBool(row, 'DryRunFault', 'dryRunFault'),
    overloadFault: rawBool(row, 'OverloadFault', 'overloadFault'),
    overVoltageFault: rawBool(row, 'OverVoltageFault', 'overVoltageFault'),
    underVoltageFault: rawBool(row, 'UnderVoltageFault', 'underVoltageFault'),
    reversePhaseFault: rawBool(row, 'ReversePhaseFault', 'reversePhaseFault'),
    phaseDownFault: rawBool(row, 'PhaseDownFault', 'phaseDownFault'),
    overHeatFault: rawBool(row, 'OverHeatFault', 'overHeatFault'),
  };

  return pump;
}

export interface DeviceConfig {
  deviceId: number;
  [k: string]: unknown;
}

export interface GeographyNode {
  id: number;
  name: string;
  [k: string]: unknown;
}

export interface ServiceEngineer {
  id: number;
  name: string;
  [k: string]: unknown;
}

export interface ChecklistTemplateItem {
  id: number;
  key: string;
  label: string;
  [k: string]: unknown;
}

/** GET /pending-pumps → list of pumps awaiting field test (normalized to camelCase). */
export async function getPendingPumps(): Promise<InstallerPump[]> {
  const { data } = await installerHttp.get<{ data: unknown[] }>('/pending-pumps');
  return (unwrap(data) ?? []).map(normalizePump);
}

/** GET /pumps/search?q= → matching pumps (normalized to camelCase). */
export async function searchPumps(q: string): Promise<InstallerPump[]> {
  const { data } = await installerHttp.get<{ data: unknown[] }>('/pumps/search', {
    params: { q },
  });
  return (unwrap(data) ?? []).map(normalizePump);
}

/** GET /devices/:id/config → device configuration. */
export async function getDeviceConfig(id: number | string): Promise<DeviceConfig> {
  const { data } = await installerHttp.get<{ data: DeviceConfig }>(
    `/devices/${encodeURIComponent(String(id))}/config`,
  );
  return unwrap(data);
}

/** GET /checklist/template → installation checklist template. */
export async function getChecklistTemplate(): Promise<ChecklistTemplateItem[]> {
  const { data } = await installerHttp.get<{ data: ChecklistTemplateItem[] }>(
    '/checklist/template',
  );
  return unwrap(data) ?? [];
}

// ── Geography getters ─────────────────────────────────────────────────────
// Server prefers the new-app geography module; params mirror the legacy
// cascade (state → district → block → village/feeder) and an optional search.

export interface GeographyQuery {
  stateId?: number | string;
  districtId?: number | string;
  blockId?: number | string;
  search?: string;
}

/**
 * Map the camelCase query helper to the server's PascalCase legacy query keys
 * (`StateId`/`DistrictId`/`BlockId`/`SearchText` — see installer.read.routes.ts).
 * Undefined values are dropped so axios doesn't serialize empty params.
 */
function geographyParams(q: GeographyQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (q.stateId !== undefined) out.StateId = q.stateId;
  if (q.districtId !== undefined) out.DistrictId = q.districtId;
  if (q.blockId !== undefined) out.BlockId = q.blockId;
  if (q.search !== undefined) out.SearchText = q.search;
  return out;
}

export async function getStates(search?: string): Promise<GeographyNode[]> {
  const { data } = await installerHttp.get<{ data: GeographyNode[] }>('/geography/states', {
    params: geographyParams({ search }),
  });
  return unwrap(data) ?? [];
}

export async function getDistricts(q: GeographyQuery): Promise<GeographyNode[]> {
  const { data } = await installerHttp.get<{ data: GeographyNode[] }>('/geography/districts', {
    params: geographyParams(q),
  });
  return unwrap(data) ?? [];
}

export async function getBlocks(q: GeographyQuery): Promise<GeographyNode[]> {
  const { data } = await installerHttp.get<{ data: GeographyNode[] }>('/geography/blocks', {
    params: geographyParams(q),
  });
  return unwrap(data) ?? [];
}

export async function getVillages(q: GeographyQuery): Promise<GeographyNode[]> {
  const { data } = await installerHttp.get<{ data: GeographyNode[] }>('/geography/villages', {
    params: geographyParams(q),
  });
  return unwrap(data) ?? [];
}

export async function getFeeders(q: GeographyQuery): Promise<GeographyNode[]> {
  const { data } = await installerHttp.get<{ data: GeographyNode[] }>('/geography/feeders', {
    params: geographyParams(q),
  });
  return unwrap(data) ?? [];
}

// GetServiceEngineers takes no params server-side; send none.
export async function getServiceEngineers(): Promise<ServiceEngineer[]> {
  const { data } = await installerHttp.get<{ data: ServiceEngineer[] }>('/service-engineers');
  return unwrap(data) ?? [];
}

// ── Writes ────────────────────────────────────────────────────────────────

/**
 * Installation create payload. Field names follow the legacy
 * createInstallation contract (plan §1.3) — the server maps them to the legacy
 * ASMX call (FCM dual-write) or the new-app copy (contract).
 *
 * `clientDraftId` is the app-generated idempotency key; the server upserts by
 * it and the app sends it as the Idempotency-Key header.
 */
export interface CreateInstallationPayload {
  clientDraftId: string;
  villageId: number | string;
  farmerName: string;
  fatherName?: string;
  farmerContactNumber: string;
  latitude: number;
  longitude: number;
  farmerCasteCategory?: string;
  landArea?: number;
  landType?: string;
  feederId?: number | string;
  imeiNumber: string;
  simOperator?: string;
  pumpType?: string;
  pumpHP?: number;
  yearOfPumpInstallation?: number;
  existingAutomationPanel?: 0 | 1;
  existingCapacitorAvailable?: 0 | 1;
  coTechnician1?: string;
  coTechnician2?: string;
  /**
   * Total pumps owned by the farmer (legacy `TotalFarmerPumps` createInstallation
   * param). Captured on the install form but outside the shared form model; the
   * server maps it to the legacy broker. First-class optional so the screen
   * doesn't have to go through the index signature.
   */
  total_farmer_pumps?: number | string;
  [k: string]: unknown;
}

export interface CreateInstallationResponse {
  /** New-app installer record ref (used to key photos / field-test). */
  ref: string;
  /** Legacy PumpId captured from the legacy createInstallation (FCM only). */
  legacyPumpId?: number | null;
}

/** POST /installations → create (dual-write FCM / new-app-only contract). */
export async function createInstallation(
  payload: CreateInstallationPayload,
): Promise<CreateInstallationResponse> {
  const { data } = await installerHttp.post<{ data: CreateInstallationResponse }>(
    '/installations',
    payload,
    { headers: { 'Idempotency-Key': payload.clientDraftId } },
  );
  return unwrap(data);
}

export type InstallerPhotoType = 'Installation' | 'Discharge';

export interface UploadPhotoResponse {
  /** Server confirms the photo was stored to S3 (canonical for both personas). */
  stored: boolean;
}

/**
 * POST /installations/:ref/photos — multipart photo upload.
 * S3 is canonical (both personas); the server forwards FCM photos to the
 * legacy UploadPhotos.ashx via outbox. `file` is a Blob/File captured by the
 * camera composable.
 */
export async function uploadInstallationPhoto(
  ref: string,
  file: Blob | File,
  type: InstallerPhotoType,
): Promise<UploadPhotoResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('type', type);
  const { data } = await installerHttp.post<{ data: UploadPhotoResponse }>(
    `/installations/${encodeURIComponent(ref)}/photos`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(data);
}

/**
 * Checklist submit body — flat booleans, matching the server `checklistSchema`
 * (snake_case, plan §1.3 #13). `ref` is the path param (not part of the body).
 */
export interface ChecklistPayload {
  device_id: number;
  technician_name: string;
  sim_activated: boolean;
  antenna_connected: boolean;
  wiring_correct: boolean;
  theft_sensor: boolean;
  relay_working: boolean;
  battery_switch_on: boolean;
  pf_check: boolean;
  discharge_verified: boolean;
  remote_control_working: boolean;
  geo_photos_captured: boolean;
  reporting_submitted: boolean;
}

export interface ChecklistResponse {
  ok: boolean;
}

/** POST /installations/:ref/checklist → submit checklist. */
export async function submitChecklist(
  ref: string,
  payload: ChecklistPayload,
): Promise<ChecklistResponse> {
  const { data } = await installerHttp.post<{ data: ChecklistResponse }>(
    `/installations/${encodeURIComponent(ref)}/checklist`,
    payload,
  );
  return unwrap(data);
}

export interface FinaliseResponse {
  ok: boolean;
}

/** POST /installations/:ref/finalise → FinaliseInstallation(PumpId, ChecklistIds). */
export async function finalise(
  ref: string,
  checklistIds: Array<number | string>,
): Promise<FinaliseResponse> {
  const { data } = await installerHttp.post<{ data: FinaliseResponse }>(
    `/installations/${encodeURIComponent(ref)}/finalise`,
    { checklistIds },
  );
  return unwrap(data);
}

/**
 * Field-test status + discharge (plan §1.3 #16, #17). Matches the server
 * `fieldTestSchema` — both fields required (UpdateFieldTestStatus +
 * UpdateDischarge are issued together server-side).
 */
export interface FieldTestPayload {
  /** Measured discharge (>= 0, 2dp); mapped to legacy UpdateDischarge. */
  discharge: number;
  /** Effective-from date for the discharge reading (legacy Wef). */
  wef: string;
}

export interface FieldTestResponse {
  ok: boolean;
}

/** POST /installations/:ref/field-test → status + discharge. */
export async function submitFieldTest(
  ref: string,
  payload: FieldTestPayload,
): Promise<FieldTestResponse> {
  const { data } = await installerHttp.post<{ data: FieldTestResponse }>(
    `/installations/${encodeURIComponent(ref)}/field-test`,
    payload,
  );
  return unwrap(data);
}

export interface CommandResponse {
  ok: boolean;
}

/**
 * POST /pumps/:id/command → start/stop (FCM → legacy sendCommandToPump;
 * Contract → new-app native pipeline or 403). e.g. command='PumpAction',
 * value='On' | 'Off'.
 */
export async function sendCommand(
  pumpId: number | string,
  command: string,
  value: string,
): Promise<CommandResponse> {
  const { data } = await installerHttp.post<{ data: CommandResponse }>(
    `/pumps/${encodeURIComponent(String(pumpId))}/command`,
    { command, value },
  );
  return unwrap(data);
}
